type Theme = "light" | "dark";

const STORAGE_KEY = "shopfront-developer-theme";

function isTheme(value: string | null): value is Theme {
    return value === "light" || value === "dark";
}

const prefersDarkQuery = window.matchMedia("(prefers-color-scheme: dark)");

function apply(theme: string | null): void {
    const root = document.documentElement;

    if(isTheme(theme)) {
        root.setAttribute("data-theme", theme);
    } else {
        // No stored override (or an explicit "system" choice) - let the CSS's own
        // prefers-color-scheme media query decide.
        root.removeAttribute("data-theme");
    }

    // Tailwind's `dark:` variant is class-based (@custom-variant dark in tailwind/tailwind.css),
    // so shared components using it (e.g. @onshopfront/components' Select) need this class too.
    const isDark = theme === "dark" || (!isTheme(theme) && prefersDarkQuery.matches);

    root.classList.toggle("dark", isDark);

    // Unlike the applied theme above, the pressed option tracks what was *selected* rather than
    // what it resolved to - "system" stays pressed even though it renders as light or dark.
    const selected = isTheme(theme) ? theme : "system";

    document.querySelectorAll<HTMLButtonElement>("[data-theme-option]").forEach(option => {
        option.setAttribute("aria-pressed", String(option.getAttribute("data-theme-option") === selected));
    });
}

export function initThemeToggle(): void {
    document.querySelectorAll<HTMLButtonElement>("[data-theme-option]").forEach(option => {
        option.addEventListener("click", () => {
            const next = option.getAttribute("data-theme-option");

            window.localStorage.setItem(STORAGE_KEY, next ?? "");
            apply(next);
        });
    });

    // Keeps the "dark" class in sync with the OS preference while "system" is selected.
    prefersDarkQuery.addEventListener("change", () => {
        apply(window.localStorage.getItem(STORAGE_KEY));
    });

    apply(window.localStorage.getItem(STORAGE_KEY));
}
